import { useState } from "react";
import { isWidthAndHeightValid } from "../../ArtworkMultiSize/utils";
import ErrorTypes from "../../error-types";

const noop = () => {};

const createError = (message, type) => ({ message, type });

// eslint-disable-next-line max-lines-per-function
export default function useArtworkValidation({
  minWidth = 0,
  minHeight = 0,
  maxWidth = 100,
  maxHeight = 100,
  isPrintAsIs = false,
  metadata = null,
  originalDimensions = {},
  onValidation = noop
} = {}) {
  const [errors, setErrors] = useState({});
  const [currentItem, setCurrentItem] = useState(null);
  const [originalArtworkDimensions] = useState(originalDimensions);

  function validationErrorType(id) {
    return errors[id]?.type;
  }

  function handleValidationError(id, type, message) {
    if (validationErrorType(id) !== type && currentItem?.hasImage) {
      onValidation("image_validation_error", type, {});
    }
    return createError(message, type);
  }

  function widthConstraint() {
    return metadata?.aspectRatio > 1 ? metadata.maxSheetHeight : metadata.maxSheetWidth;
  }

  function heightConstraint() {
    return metadata?.aspectRatio > 1 ? metadata.maxSheetWidth : metadata.maxSheetHeight;
  }

  function fitsGangSheet({ width, height }) {
    return (
      width !== "" &&
      height !== "" &&
      Math.min(width, height) <= metadata?.maxSheetWidth &&
      Math.max(width, height) <= metadata?.maxSheetHeight
    );
  }

  function invalidWidthValidation(item, errorsMap) {
    if (item.width === "") {
      errorsMap[item.id] = handleValidationError(
        item.id,
        ErrorTypes.INVALID_WIDTH,
        "Please enter a valid width."
      );
      return true;
    }
    return false;
  }

  function invalidHeightValidation(item, errorsMap) {
    if (item.height === "") {
      errorsMap[item.id] = handleValidationError(
        item.id,
        ErrorTypes.INVALID_HEIGHT,
        `Please enter a valid ${isPrintAsIs ? "length" : "height"}.`
      );
      return true;
    }
    return false;
  }

  function negativeValuesValidation(item, errorsMap) {
    if (item.width < 0 || item.height < 0) {
      errorsMap[item.id] = handleValidationError(
        item.id,
        ErrorTypes.NEGATIVE_VALUES,
        "Negative values are not permitted."
      );
      return true;
    }
    return false;
  }

  function minPrintSizeValidation(item, errorsMap) {
    if (!(item.width >= minWidth && item.height >= minHeight)) {
      errorsMap[item.id] = handleValidationError(
        item.id,
        ErrorTypes.MIN_PRINT_SIZE,
        `The minimum print size is ${minWidth}" x ${minHeight}${isPrintAsIs ? "'" : '"'}`
      );
      return true;
    }
    return false;
  }

  function maxPrintSizeValidation(item, errorsMap) {
    if (
      !isPrintAsIs &&
      !fitsGangSheet(item) &&
      !(Number(item.width) <= maxWidth && Number(item.height) <= maxHeight)
    ) {
      errorsMap[item.id] = handleValidationError(
        item.id,
        ErrorTypes.MAX_PRINT_SIZE,
        `The maximum print size is ${maxWidth}" x ${maxHeight}"`
      );
      return true;
    }
    return false;
  }

  function maxPrintSizePrintAsIsValidation(item, errorsMap) {
    if (isPrintAsIs && item.height > (metadata?.maxSheetHeight || maxHeight)) {
      errorsMap[item.id] = handleValidationError(
        item.id,
        ErrorTypes.MAX_PRINT_SIZE_PRINT_AS_IS,
        `The maximum print size is ${minWidth}" x ${maxHeight}'`
      );
      return true;
    }
    return false;
  }

  function willFitGangSheet(item, errorsMap) {
    if (!metadata) return false;
    const isWidthValid = fitsGangSheet(item)
      ? isWidthAndHeightValid(item)
      : item.width !== "" && item.width <= widthConstraint();
    const isHeightValid = fitsGangSheet(item)
      ? isWidthAndHeightValid(item)
      : item.height !== "" && item.height <= heightConstraint();

    if (isWidthValid && isHeightValid) return false;

    errorsMap[item.id] = errorsMap[item.id]
      ? { ...errorsMap[item.id], isFieldValid: isWidthValid && isHeightValid }
      : { isFieldValid: isWidthValid && isHeightValid };

    return true;
  }

  function resetErrors() {
    setErrors({});
  }

  function validateDimensions(item) {
    const newErrors = {};

    const hasError = [
      invalidWidthValidation,
      invalidHeightValidation,
      negativeValuesValidation,
      maxPrintSizePrintAsIsValidation,
      minPrintSizeValidation,
      maxPrintSizeValidation
    ].some(validation => validation(item, newErrors));

    if (!hasError) {
      willFitGangSheet(item, newErrors);
    }

    setCurrentItem(item);
    setErrors(newErrors);

    return newErrors[item.id];
  }

  return {
    errors,
    currentItem,
    originalArtworkDimensions,
    validateDimensions,
    resetErrors
  };
}
